'use client';

import React, {
  type Dispatch,
  type SetStateAction,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

type CurrentFilterStatusProps = {
  children: ReactNode;
};

type CurrentFilterStatusContextType = {
  currentFiltersStatus?: TransportFiltersResponse;
  setCurrentFiltersStatus: Dispatch<SetStateAction<TransportFiltersResponse>>;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  visibleTransports: AvailableTransportEndpointResponse;
  setVisibleTransports: Dispatch<SetStateAction<AvailableTransportEndpointResponse>>;
};

const CurrentFilterStatusContext = createContext<CurrentFilterStatusContextType>({
  page: 1,
  setCurrentFiltersStatus: () => null,
  setPage: () => null,
  visibleTransports: [],
  setVisibleTransports: () => null,
});

export function CurrentFilterStatusProvider(props: CurrentFilterStatusProps) {
  const { children } = props;
  const [currentFiltersStatus, setCurrentFiltersStatus] = useState<TransportFiltersResponse>({});
  const [page, setPage] = useState(1);
  const [visibleTransports, setVisibleTransports] = useState<AvailableTransportEndpointResponse>([]);

  const memoizedValue = useMemo(
    () => ({
      currentFiltersStatus,
      setCurrentFiltersStatus,
      page,
      setPage,
      visibleTransports,
      setVisibleTransports,
    }),
    [currentFiltersStatus, setCurrentFiltersStatus, page, setPage, visibleTransports, setVisibleTransports]
  );

  return <CurrentFilterStatusContext.Provider value={memoizedValue}>{children}</CurrentFilterStatusContext.Provider>;
}

export function useCurrentFilterStatus() {
  const context = useContext(CurrentFilterStatusContext);

  if (context === undefined) {
    throw new Error('useCurrentFilterStatus must be within a CurrentFilterStatusProvider');
  }
  return context;
}
